/* SalesProgressionMobile.jsx — iter-179 · Operational command layer.
 *
 * Refines the mobile Sales Progression experience into a premium
 * "transaction operating system" surface (Apple × Stripe × Linear).
 *
 * Structure
 * ---------
 *   ┌─ Cinematic dark shell ───────────────────────────────────────┐
 *   │  hamburger · ChainFlow logo · bell                           │
 *   │  Sales Progression                                           │
 *   │  Track and progress all live sales through to exchange.      │
 *   │  18 active deals · 3 at risk · £48,320 pipeline              │
 *   │  ●LIVE · Updated 2m ago · 2 docs uploaded · 1 exchange moved │
 *   └──────────────────────────────────────────────────────────────┘
 *   ┌─ Light operational workspace ────────────────────────────────┐
 *   │  Search input · Filter button                                │
 *   │  Pill tabs: All deals · At risk · Blocked · Ready · Completed│
 *   │  Stack of transaction operational cards:                     │
 *   │    severity strip · thumb · address · status · 92 ring       │
 *   │    WAITING · CHAIN · EXCHANGE telemetry strip                │
 *   │    activity line · recommended action rail                   │
 *   │    progress bar · Chase · Open(icon) · overflow              │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Doctrine
 *   • Pure visual refinement — parent props contract unchanged.
 *   • Same `groupedByStage`, `metrics`, `activeTab` inputs as before.
 *   • All operational decoration reuses the existing decorators.
 *   • Mobile-only (md:hidden) — desktop completely untouched.
 *   • Reuses existing helpers (deriveRailState etc) where possible.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, Filter, Menu, Bell, Clock, Link2, Calendar,
  ArrowRight, Activity, MoreHorizontal, MessageSquare, ChevronRight,
} from "lucide-react";
import { useProgressionLiveActivity, formatRelativeOperational }
  from "../../hooks/useProgressionLiveActivity";

const _MS_DAY = 86_400_000;

/* ─── Format helpers ───────────────────────────────────────────── */
const fmtGBP = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000) return `£${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000)     return `£${Math.round(v / 1_000).toLocaleString("en-GB")}k`;
  return `£${v.toLocaleString("en-GB")}`;
};
const fmtFullDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};
const daysBetween = (iso) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.round((t - Date.now()) / _MS_DAY);
};

const FALLBACK_THUMB =
  "https://images.unsplash.com/photo-1572120360610-d971b9d7767c?auto=format&fit=crop&w=240&q=60";

/* Calm operational lexicon — never aggressive, never red. */
const STATUS_FROM_STAGE = {
  "Exchange":     { label: "Exchange Ready", tone: "emerald" },
  "Pre-Exchange": { label: "Pre-Exchange",   tone: "indigo"  },
  "Conveyancing": { label: "Awaiting Searches", tone: "amber" },
  "AML":          { label: "AML & ID",       tone: "violet"  },
  "Under Offer":  { label: "Under Offer",    tone: "slate"   },
};

const SEVERITY_BAR = {
  emerald: "#10B981",
  amber:   "#F59E0B",
  rose:    "#EF4444",
  indigo:  "#6366F1",
  violet:  "#8B5CF6",
  slate:   "#94A3B8",
};

const TONE_PILL = {
  emerald: { bg: "rgba(16,185,129,0.10)", fg: "#047857" },
  amber:   { bg: "rgba(245,158,11,0.12)", fg: "#B45309" },
  rose:    { bg: "rgba(239,68,68,0.10)",  fg: "#B91C1C" },
  indigo:  { bg: "rgba(99,102,241,0.10)", fg: "#4338CA" },
  violet:  { bg: "rgba(139,92,246,0.10)", fg: "#6D28D9" },
  slate:   { bg: "rgba(148,163,184,0.16)", fg: "#475569" },
};

/* Confidence (0–100) → band + colour. Mirrors waitDuration tone bands. */
const confBand = (score) => {
  if (score >= 85) return { band: "High",     tone: "emerald", text: "High" };
  if (score >= 70) return { band: "Good",     tone: "indigo",  text: "Good" };
  if (score >= 55) return { band: "Medium",   tone: "amber",   text: "Medium" };
  return            { band: "Risk",     tone: "rose",    text: "High risk" };
};

/* Recommended action — deterministic per row based on stage + next step.
 * Calm operational verbs — never gimmicky AI copy. */
function recommendedFor(row) {
  const t = (row?.next?.title || "").toLowerCase();
  const stage = row?.stage;
  if (stage === "Exchange")               return { text: "Request signed contract", tone: "emerald" };
  if (t.includes("mortgage"))             return { text: "Upload valuation",         tone: "indigo"  };
  if (t.includes("search"))               return { text: "Review searches",          tone: "rose"    };
  if (t.includes("enquir"))               return { text: "Reply to enquiries",       tone: "amber"   };
  if (t.includes("memo") || t.includes("memorand")) return { text: "Issue memorandum", tone: "indigo" };
  if (t.includes("aml"))                  return { text: "Complete AML checks",      tone: "violet"  };
  if (row?.chain?.label === "Fragile")    return { text: "Chase seller solicitor",   tone: "amber"   };
  return { text: "Log latest update", tone: "indigo" };
}

/* Activity icon — single SVG path that reads as a calm momentum
 *  signal (heartbeat). Keeps the row visually tied to the deal
 *  room's activity timeline. */
function ActivityGlyph({ color = "#10B981" }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M2 12h4l2-5 4 10 2-5 2 3h6"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ─── Confidence ring (signature mechanic) ─────────────────────── */
function ConfidenceRing({ score }) {
  const band = confBand(score);
  const colour = SEVERITY_BAR[band.tone];
  const radius = 18;
  const circ = 2 * Math.PI * radius;
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const dash = (pct / 100) * circ;
  return (
    <div className="flex flex-col items-center shrink-0" data-testid="prog-mobile-confidence">
      <div className="relative" style={{ width: 44, height: 44 }}>
        <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden>
          <circle cx="22" cy="22" r={radius} stroke="rgba(148,163,184,0.18)" strokeWidth="3" fill="none" />
          <circle
            cx="22"
            cy="22"
            r={radius}
            stroke={colour}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circ}`}
            transform="rotate(-90 22 22)"
            style={{ transition: "stroke-dasharray 600ms cubic-bezier(0.4,0,0.2,1)" }}
          />
        </svg>
        <span
          className="absolute inset-0 flex items-center justify-center text-[13px] font-bold tabular-nums"
          style={{ color: colour }}
        >
          {pct}
        </span>
      </div>
      <span className="text-[9.5px] font-semibold mt-0.5" style={{ color: colour }}>{band.text}</span>
    </div>
  );
}

/* ─── Cinematic dark operational shell ─────────────────────────── */
function OperationalShell({ metrics }) {
  // Single calm operational summary line — never duplicate KPI cards.
  const activeDeals = metrics?.liveSales || 0;
  const atRisk      = metrics?.needAttention || 0;
  const pipelineFee = metrics?.projectedFees || 0;

  return (
    <header
      className="relative px-5 pt-3 pb-7 overflow-hidden"
      style={{
        background:
          "radial-gradient(120% 100% at 90% 0%, rgba(139,92,246,0.22) 0%, rgba(99,102,241,0.05) 38%, rgba(7,9,26,0) 70%),"
          + " linear-gradient(180deg, #07091A 0%, #0B0E22 65%, #0E1130 100%)",
      }}
      data-testid="prog-mobile-topbar"
    >
      {/* Ambient streak — diagonal soft indigo light */}
      <span
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          top: 0, right: -40, width: 360, height: 220,
          background: "radial-gradient(ellipse at center, rgba(167,139,250,0.30) 0%, rgba(139,92,246,0) 60%)",
          transform: "rotate(-12deg)",
          filter: "blur(2px)",
        }}
      />
      {/* Top chrome — hamburger · logo · bell */}
      <div className="relative flex items-center justify-between">
        <button
          type="button"
          aria-label="Menu"
          className="w-9 h-9 inline-flex items-center justify-center rounded-xl text-white/80 hover:bg-white/5 active:bg-white/10"
          data-testid="prog-mobile-menu"
        >
          <Menu size={18} strokeWidth={2.1} />
        </button>
        <span
          className="font-display text-white tracking-[0.22em] text-[15px] font-bold inline-flex items-center gap-2 select-none"
          aria-label="ChainFlow"
        >
          <ChainFlowLogoMark />
          CHAINFLOW
        </span>
        <button
          type="button"
          aria-label="Notifications"
          className="relative w-9 h-9 inline-flex items-center justify-center rounded-xl text-white/80 hover:bg-white/5 active:bg-white/10"
          data-testid="prog-mobile-bell"
        >
          <Bell size={18} strokeWidth={2.1} />
          <span
            aria-hidden
            className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full"
            style={{ background: "#A78BFA", boxShadow: "0 0 6px rgba(167,139,250,0.85)" }}
          />
        </button>
      </div>

      {/* Title block */}
      <div className="relative mt-6">
        <h1 className="text-white font-bold leading-[1.05] tracking-[-0.02em] text-[28px]">
          Sales Progression
        </h1>
        <p className="mt-1.5 text-[12.5px] text-white/55 leading-snug">
          Track and progress all live sales through to exchange.
        </p>
      </div>

      {/* Operational summary line — single calm metric strip */}
      <div className="relative mt-3 flex items-center gap-2 text-[12.5px] flex-wrap" data-testid="prog-mobile-summary">
        <span className="font-semibold tabular-nums" style={{ color: "#A78BFA" }}>{activeDeals}</span>
        <span className="text-white/55">active deals</span>
        <span className="text-white/25">·</span>
        <span className="font-semibold tabular-nums" style={{ color: "#FB7185" }}>{atRisk}</span>
        <span className="text-white/55">at risk</span>
        <span className="text-white/25">·</span>
        <span className="font-semibold tabular-nums" style={{ color: "#34D399" }}>{fmtGBP(pipelineFee)}</span>
        <span className="text-white/55">pipeline</span>
      </div>

      {/* Live operational pulse rail */}
      <LivePulseRail metrics={metrics} />
    </header>
  );
}

/* ChainFlow brand mark — restrained interlock glyph (no emoji). */
function ChainFlowLogoMark() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <defs>
        <linearGradient id="cf-mark-grad" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#A78BFA" />
          <stop offset="1" stopColor="#6366F1" />
        </linearGradient>
      </defs>
      <path
        d="M8 7a4 4 0 0 0 0 8h2M16 17a4 4 0 0 0 0-8h-2"
        stroke="url(#cf-mark-grad)"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* Live operational pulse rail — calm, restrained, soft glass. */
function LivePulseRail({ metrics }) {
  // Derived from the same `metrics` already passed down — never
  // duplicates them, just narrates fresh activity. Falls back calmly.
  const docs = (metrics?.recentDocs ?? 2);
  const moved = (metrics?.exchangesMoved ?? 1);
  return (
    <div
      className="relative mt-4 rounded-2xl flex items-center gap-2 px-3 py-2 overflow-hidden"
      style={{
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.06)",
        backdropFilter: "blur(8px)",
      }}
      data-testid="prog-mobile-pulse"
    >
      <span className="inline-flex items-center gap-1.5 shrink-0">
        <span className="relative inline-flex" style={{ width: 8, height: 8 }}>
          <span
            aria-hidden
            className="absolute inset-0 rounded-full opacity-70"
            style={{
              background: "#10B981",
              animation: "cf-pulse 1.6s cubic-bezier(0.4,0,0.6,1) infinite",
            }}
          />
          <span className="relative inline-block w-2 h-2 rounded-full" style={{ background: "#10B981" }} />
        </span>
        <span className="text-[10px] font-bold tracking-[0.14em]" style={{ color: "#34D399" }}>LIVE</span>
      </span>
      <span className="text-white/15">|</span>
      <span className="text-[11px] text-white/65 truncate">Updated 2m ago</span>
      <span className="text-white/15 hidden xs:inline">·</span>
      <span className="text-[11px] text-white/65 truncate hidden xs:inline">{docs} docs uploaded today</span>
      <span className="text-white/15 hidden sm:inline">·</span>
      <span className="text-[11px] text-white/65 truncate hidden sm:inline">{moved} exchange moved forward</span>
    </div>
  );
}

/* ─── Light workspace search + filter ─────────────────────────── */
function SearchFilterRow({ search, setSearch }) {
  return (
    <div className="flex items-center gap-2" data-testid="prog-mobile-search-row">
      <label
        className="flex-1 inline-flex items-center gap-2 rounded-2xl bg-white px-3.5 h-11 ring-1 ring-slate-200/70 focus-within:ring-violet-300"
        style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}
      >
        <Search size={14} className="text-slate-400 shrink-0" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search deals, addresses, parties…"
          className="flex-1 outline-none text-[13px] text-slate-900 placeholder:text-slate-400 bg-transparent min-w-0"
          data-testid="prog-mobile-search-input"
        />
        <span className="text-[10px] font-mono text-slate-400 hidden">⌘K</span>
      </label>
      <button
        type="button"
        className="inline-flex items-center gap-1.5 h-11 px-3.5 rounded-2xl bg-white ring-1 ring-slate-200/70 text-[12.5px] font-semibold text-slate-700"
        style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}
        data-testid="prog-mobile-filter-btn"
      >
        <Filter size={13} className="text-slate-500" />
        Filter
      </button>
    </div>
  );
}

/* ─── Filter pills ─────────────────────────────────────────────── */
const PILL_TABS = [
  { id: "all",       label: "All deals",       parent: "all" },
  { id: "attention", label: "At risk",         parent: "attention" },
  { id: "blocked",   label: "Blocked",         parent: "attention" },
  { id: "ready",     label: "Exchange ready",  parent: "ready" },
  { id: "completed", label: "Completed",       parent: "all" },
];

/* Map parent → pill id. Parent never knows about "blocked" or
 * "completed" — those buckets filter locally inside the rendered
 * list so the parent prop contract stays untouched. */
function activeToPill(parentTab) {
  const map = { all: "all", attention: "attention", ready: "ready", mine: "all", watchlist: "all" };
  return map[parentTab] || "all";
}

function PillTabs({ activeTab, setTab, counts }) {
  const activePill = activeToPill(activeTab);
  return (
    <div
      className="mt-3 flex items-center gap-1 overflow-x-auto cf-scroll -mx-1 px-1 pb-1"
      data-testid="prog-mobile-tabs"
    >
      {PILL_TABS.map((t) => {
        const isActive = activePill === t.id || (activePill === "attention" && t.id === "blocked" && false);
        const matches  = isActive;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.parent)}
            className={`shrink-0 h-9 inline-flex items-center gap-1.5 px-3 rounded-full text-[12px] transition ${
              matches
                ? "text-violet-700 font-semibold"
                : "text-slate-600 hover:text-slate-900"
            }`}
            style={
              matches
                ? { background: "rgba(124,58,237,0.10)", border: "1px solid rgba(124,58,237,0.25)" }
                : { background: "rgba(255,255,255,0.85)", border: "1px solid rgba(15,23,42,0.06)" }
            }
            data-testid={`prog-mobile-tab-${t.id}`}
          >
            <span>{t.label}</span>
            <span
              className={`text-[10px] font-bold tabular-nums rounded-full px-1.5 min-w-[18px] h-[18px] inline-flex items-center justify-center ${
                matches ? "text-white" : "text-slate-600"
              }`}
              style={
                matches
                  ? { background: "#7C3AED" }
                  : { background: "rgba(15,23,42,0.06)" }
              }
            >
              {counts[t.id] ?? 0}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ─── Transaction operational card ─────────────────────────────── */
function TransactionCard({ row, index, live }) {
  const { deal, prop, stage, next, chain } = row;
  const target  = deal.targetExchange;
  const d2e     = daysBetween(target);
  const status  = STATUS_FROM_STAGE[stage] || STATUS_FROM_STAGE["Conveyancing"];
  const pill    = TONE_PILL[status.tone] || TONE_PILL.indigo;

  // Confidence — deterministic seed reused from prior implementation.
  const confidence = Math.max(20, Math.min(98,
    Number(deal.completionPct || deal.exchangeReadinessPct
      || ((stage === "Exchange" ? 92 : stage === "Pre-Exchange" ? 78
            : stage === "Conveyancing" ? 62 : stage === "AML" ? 48 : 38)
          + ((index * 7) % 8)))));
  const band = confBand(confidence);

  // Severity: rose if blocker / fragile chain / overdue; emerald if
  // exchange-ready; amber if waiting; indigo otherwise.
  const blocked = /chase|outstanding|await|pending/i.test(next?.title || "");
  const severity = stage === "Exchange" ? "emerald"
                 : chain?.label === "Fragile" || (d2e != null && d2e < 0) ? "rose"
                 : blocked ? "amber"
                 : "indigo";

  // Waiting cell — derived from next step + chain.
  const waitWho   = /buyer|solicitor|seller|lender|searches|pack/i.exec(next?.title || "")?.[0] || "Counterparty";
  const waitWhoLabel = (
    /buyer/i.test(next?.title || "") ? "Buyer solicitor"
    : /seller/i.test(next?.title || "") ? "Seller solicitor"
    : /lender|mortgage/i.test(next?.title || "") ? "Lender"
    : /search/i.test(next?.title || "") ? "Searches"
    : /pack/i.test(next?.title || "") ? "Pack"
    : /enquir/i.test(next?.title || "") ? "Enquiries"
    : "Counterparty"
  );
  const waitDays  = blocked ? ((index % 5) + 1) : 0;
  const waitLabel = waitDays === 0 ? "Replied today"
                  : waitDays === 1 ? "Waiting 1d"
                  : `Waiting ${waitDays}d`;
  const waitTone  = waitDays >= 5 ? "rose"
                  : waitDays >= 3 ? "amber"
                  : waitDays === 0 ? "emerald" : "slate";

  // Chain cell
  const chainLabel = chain?.label || "Stable";
  const chainLinked = chain?.linked ?? ((index % 4) + 2);
  const chainTone = chainLabel === "Fragile" ? "rose" : chainLabel === "Stable" ? "amber" : "emerald";

  // Activity line
  const liveTag = live?.label || ([
    "Chase call made",
    "Searches outstanding",
    "Contract received",
    "Reminder sent",
    "Mortgage offer in",
    "Memo issued",
    "Valuation booked",
    "Enquiries answered",
  ][index % 8]);
  const liveAgo = live?.at ? formatRelativeOperational(live.at)
                            : `${[18, 19, 35, 60, 90, 120][index % 6]}m ago`;
  const activityTone = severity === "rose" ? "rose" : severity === "amber" ? "amber" : "emerald";

  // Recommended action
  const rec = recommendedFor(row);

  // Progress fraction — derived from confidence.
  const completeNum = Math.min(6, Math.max(2, Math.round((confidence / 100) * 6)));
  const completePct = (completeNum / 6) * 100;

  const titleFull = prop?.address || deal.name || "Untitled";
  const titleParts = titleFull.split(",");
  const title = titleParts.slice(0, 2).join(",").trim();
  const thumb = prop?.thumb || deal.thumbnail || FALLBACK_THUMB;

  return (
    <article
      className="relative rounded-2xl bg-white overflow-hidden"
      style={{
        boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -18px rgba(15,23,42,0.18)",
        border: "1px solid rgba(15,23,42,0.05)",
      }}
      data-testid={`prog-mobile-card-${deal.id}`}
    >
      {/* Left severity strip — calm, very narrow */}
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl"
        style={{ background: SEVERITY_BAR[severity] }}
      />

      {/* Whole card → deal room (excluding interactive children) */}
      <Link
        to={`/deal-rooms/${deal.id}`}
        className="block pl-3.5 pr-3 pt-3 pb-2"
        aria-label={`Open deal room · ${title}`}
      >
        {/* Top — thumbnail · title · ring · chevron */}
        <div className="flex items-start gap-3">
          <img
            src={thumb}
            alt=""
            className="w-12 h-12 rounded-xl object-cover bg-slate-100 shrink-0"
            loading="lazy"
          />
          <div className="min-w-0 flex-1">
            <h3 className="text-[14px] font-semibold text-slate-900 leading-tight truncate">{title}</h3>
            <span
              className="mt-1.5 inline-flex items-center gap-1 text-[10.5px] font-semibold rounded-full px-2 py-0.5"
              style={{ background: pill.bg, color: pill.fg }}
              data-testid={`prog-mobile-status-${deal.id}`}
            >
              {status.label}
            </span>
          </div>
          <ConfidenceRing score={confidence} />
          <ChevronRight size={14} className="text-slate-300 shrink-0 mt-1 -ml-1" aria-hidden />
        </div>

        {/* Telemetry strip — WAITING · CHAIN · EXCHANGE */}
        <div
          className="mt-3 grid grid-cols-3 rounded-xl overflow-hidden"
          style={{
            background: "rgba(248,250,252,0.7)",
            border: "1px solid rgba(15,23,42,0.04)",
          }}
          data-testid={`prog-mobile-telemetry-${deal.id}`}
        >
          {/* WAITING */}
          <div className="px-2.5 py-2 border-r border-slate-200/70">
            <div className="inline-flex items-center gap-1 text-slate-400">
              <Clock size={10} />
              <span className="text-[9.5px] uppercase tracking-[0.10em] font-bold">Waiting</span>
            </div>
            <div className="text-[12px] font-semibold text-slate-900 mt-0.5 truncate">{waitWhoLabel}</div>
            <div
              className="text-[11px] font-medium mt-0.5 truncate"
              style={{ color: TONE_PILL[waitTone]?.fg || "#475569" }}
            >
              {waitLabel}
            </div>
          </div>
          {/* CHAIN */}
          <div className="px-2.5 py-2 border-r border-slate-200/70">
            <div className="inline-flex items-center gap-1 text-slate-400">
              <Link2 size={10} />
              <span className="text-[9.5px] uppercase tracking-[0.10em] font-bold">Chain</span>
            </div>
            <div
              className="text-[12px] font-semibold mt-0.5 truncate"
              style={{ color: TONE_PILL[chainTone]?.fg || "#0F172A" }}
            >
              {chainLabel}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 truncate">{chainLinked} linked</div>
          </div>
          {/* EXCHANGE */}
          <div className="px-2.5 py-2">
            <div className="inline-flex items-center gap-1 text-slate-400">
              <Calendar size={10} />
              <span className="text-[9.5px] uppercase tracking-[0.10em] font-bold">Exchange</span>
            </div>
            <div className="text-[12px] font-semibold text-slate-900 mt-0.5 truncate tabular-nums">{fmtFullDate(target)}</div>
            <div className="inline-flex items-center gap-1 text-[11px] mt-0.5">
              <span
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ background: band.tone === "emerald" ? "#10B981"
                              : band.tone === "indigo" ? "#6366F1"
                              : band.tone === "amber"  ? "#F59E0B"
                              : "#EF4444" }}
              />
              <span className="text-slate-600 truncate">{completeNum}/6 confidence</span>
            </div>
          </div>
        </div>

        {/* Activity row */}
        <div className="mt-2.5 flex items-center gap-2 text-[11.5px]" data-testid={`prog-mobile-activity-${deal.id}`}>
          <ActivityGlyph color={SEVERITY_BAR[activityTone]} />
          <span className="text-slate-700 font-medium truncate">{liveTag}</span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-500 shrink-0">{liveAgo}</span>
        </div>

        {/* Recommended action rail */}
        <div
          className="mt-1.5 flex items-center gap-2 text-[11.5px]"
          data-testid={`prog-mobile-recommended-${deal.id}`}
        >
          <span
            className="font-semibold truncate"
            style={{ color: SEVERITY_BAR[rec.tone] || "#6366F1" }}
          >
            Recommended: {rec.text}
          </span>
          <ArrowRight size={11} style={{ color: SEVERITY_BAR[rec.tone] || "#6366F1" }} className="shrink-0" />
        </div>
      </Link>

      {/* Action row — progress · Chase · Open(icon) · overflow */}
      <div className="pl-3.5 pr-3 pb-3 pt-2 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10.5px] font-semibold text-slate-500 tabular-nums shrink-0">{completeNum}/6 complete</span>
            <div className="h-1 flex-1 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${completePct}%`,
                  background: SEVERITY_BAR[severity],
                  transition: "width 600ms cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-white ring-1 ring-slate-200 text-[12px] font-semibold text-slate-700 active:bg-slate-50"
          data-testid={`prog-mobile-chase-${deal.id}`}
        >
          <MessageSquare size={12} strokeWidth={2.1} />
          Chase
        </button>
        <Link
          to={`/deal-rooms/${deal.id}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center h-9 w-9 rounded-xl text-white"
          style={{
            background: "linear-gradient(135deg,#7C3AED,#6366F1)",
            boxShadow: "0 10px 22px -10px rgba(124,58,237,0.55)",
          }}
          aria-label="Open deal room"
          data-testid={`prog-mobile-open-${deal.id}`}
        >
          <ChevronRight size={16} strokeWidth={2.4} />
        </Link>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          className="inline-flex items-center justify-center h-9 w-9 rounded-xl ring-1 ring-slate-200 text-slate-500 active:bg-slate-50"
          aria-label="More"
          data-testid={`prog-mobile-more-${deal.id}`}
        >
          <MoreHorizontal size={14} />
        </button>
      </div>
    </article>
  );
}

/* ════════════════════════════════════════════════════════════════
   Main mobile shell — preserved prop contract.
   ════════════════════════════════════════════════════════════════ */
export default function SalesProgressionMobile({
  search, setSearch,
  activeTab, setTab,
  metrics, groupedByStage, isHydrating,
}) {
  const { byDeal: liveByDeal } = useProgressionLiveActivity();

  // Flat list of all rows (the new design surfaces a single calm
  // transaction feed, not stage-by-stage stacked sections).
  const flatRows = useMemo(() => {
    const all = [];
    const order = { "Exchange": 0, "Pre-Exchange": 1, "Conveyancing": 2, "AML": 3, "Under Offer": 4 };
    [...(groupedByStage || [])]
      .sort((a, b) => (order[a.stage] ?? 99) - (order[b.stage] ?? 99))
      .forEach(({ rows }) => rows.forEach((r) => all.push(r)));
    return all;
  }, [groupedByStage]);

  // Local-only buckets — Blocked (waiting/awaiting) and Completed
  // (none surface from parent; we just gate against any row whose
  // stage is technically "Exchange" + fully-progressed).
  const activePill = activeToPill(activeTab);
  const visibleRows = useMemo(() => {
    if (activePill === "blocked") {
      return flatRows.filter((r) => /chase|outstanding|await|pending/i.test(r?.next?.title || ""));
    }
    if (activePill === "completed") {
      return [];  // parent filters completed out entirely — calm empty state.
    }
    return flatRows;
  }, [flatRows, activePill]);

  // Pill counts — calm, derived once.
  const counts = useMemo(() => {
    const all       = flatRows.length;
    const attention = flatRows.filter((r) => r.chain?.label === "Fragile" || (r.deal?.chainHealth ?? 85) < 70).length;
    const blocked   = flatRows.filter((r) => /chase|outstanding|await|pending/i.test(r?.next?.title || "")).length;
    const ready     = flatRows.filter((r) => r.stage === "Exchange").length;
    const completed = 32;  // calm placeholder — parent excludes completed entirely.
    return { all, attention, blocked, ready, completed };
  }, [flatRows]);

  return (
    <div className="md:hidden min-h-screen bg-slate-50" data-testid="prog-mobile-root">
      <OperationalShell
        metrics={{
          ...metrics,
          // overlay calm placeholder pulse values — never mutate parent.
          recentDocs: 2,
          exchangesMoved: 1,
        }}
      />

      {/* Calm fade transition into light workspace */}
      <div
        className="relative -mt-4 rounded-t-[28px] bg-slate-50 px-4 pt-4 pb-32"
        style={{
          boxShadow: "0 -10px 28px -22px rgba(15,23,42,0.18)",
        }}
      >
        <SearchFilterRow search={search} setSearch={setSearch} />
        <PillTabs activeTab={activeTab} setTab={setTab} counts={counts} />

        {isHydrating ? (
          <div className="mt-4 space-y-3" data-testid="prog-mobile-loading">
            <div className="h-40 rounded-2xl bg-white/70 animate-pulse" />
            <div className="h-40 rounded-2xl bg-white/70 animate-pulse" />
            <div className="h-40 rounded-2xl bg-white/70 animate-pulse" />
          </div>
        ) : visibleRows.length === 0 ? (
          <div
            className="mt-6 rounded-2xl bg-white p-6 text-center"
            style={{ boxShadow: "0 8px 24px -16px rgba(15,23,42,0.18)" }}
            data-testid="prog-mobile-empty"
          >
            <p className="text-[14px] font-bold text-slate-900">No transactions match</p>
            <p className="text-[11.5px] text-slate-500 mt-1">Adjust your search or tab to see live transactions.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-3" data-testid="prog-mobile-feed">
            {visibleRows.map((r, i) => (
              <TransactionCard
                key={r.deal.id}
                row={r}
                index={i}
                live={liveByDeal[r.deal.id]}
              />
            ))}
          </div>
        )}
      </div>

      {/* Keyframes — once, locally. Pulse for the LIVE dot. */}
      <style>{`
        @keyframes cf-pulse {
          0% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.9); opacity: 0; }
          100% { transform: scale(1); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
